import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Equipment } from '../types';
import { getUnassignedEquipment, getClientDetails, updateClientDetails } from '../services/api';

interface EquipmentSelectorProps {
    clientId: string | null | undefined;
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({ clientId }) => {
    const [unassignedEquipment, setUnassignedEquipment] = useState<Equipment[]>([]);
    const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<string>>(new Set());
    const [isLoadingEquipment, setIsLoadingEquipment] = useState<boolean>(false);

    // Load unassigned equipment and selected equipment IDs
    useEffect(() => {
        const loadEquipmentData = async () => {
            if (!clientId) return;

            setIsLoadingEquipment(true);
            try {
                // Load unassigned equipment
                const equipmentResponse = await getUnassignedEquipment();
                if (equipmentResponse.success && equipmentResponse.equipment) {
                    setUnassignedEquipment(equipmentResponse.equipment);
                }

                // Load selected equipment IDs from client details
                const detailsResponse = await getClientDetails(clientId);
                if (detailsResponse.success && detailsResponse.data) {
                    const equipmentIds = detailsResponse.data.selected_equipment_ids || [];
                    setSelectedEquipmentIds(new Set(equipmentIds));
                }
            } catch (error) {
                console.error('Failed to load equipment data:', error);
            } finally {
                setIsLoadingEquipment(false);
            }
        };

        loadEquipmentData();
    }, [clientId]);

    const handleEquipmentToggle = async (equipmentId: string) => {
        if (!clientId) return;

        const newSelectedIds = new Set(selectedEquipmentIds);
        if (newSelectedIds.has(equipmentId)) {
            newSelectedIds.delete(equipmentId);
        } else {
            newSelectedIds.add(equipmentId);
        }
        setSelectedEquipmentIds(newSelectedIds);

        // Save to backend
        try {
            await updateClientDetails(clientId, {
                selected_equipment_ids: Array.from(newSelectedIds)
            });
        } catch (error) {
            console.error('Failed to update client details:', error);
            // Revert on error
            setSelectedEquipmentIds(selectedEquipmentIds);
        }
    };

    if (!clientId) {
        return (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-gray-600">Please create a client first to select equipment.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h3 className="font-semibold text-gray-900 mb-4">Select Equipment</h3>
                {isLoadingEquipment ? (
                    <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mr-2" />
                        <span className="text-gray-600">Loading equipment...</span>
                    </div>
                ) : unassignedEquipment.length === 0 ? (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-gray-600">No unassigned equipment available. Add equipment using the backend script.</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {unassignedEquipment.map((eq) => (
                            <label
                                key={eq.id}
                                className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedEquipmentIds.has(eq.id)}
                                    onChange={() => handleEquipmentToggle(eq.id)}
                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-gray-900 capitalize">
                                            {eq.equipment_type}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            {eq.make} {eq.model} ({eq.year})
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                        Serial: {eq.serial_number}
                                        {eq.vin && ` • VIN: ${eq.vin}`}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Appraised Value: ${eq.appraised_value.toLocaleString()} •
                                        Current Value: ${eq.current_value.toLocaleString()}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                )}
                {selectedEquipmentIds.size > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-900">
                            <strong>{selectedEquipmentIds.size}</strong> equipment item{selectedEquipmentIds.size !== 1 ? 's' : ''} selected
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EquipmentSelector;

